import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CacheKeys } from '@/lib/cache'
import { parseError } from '@/lib/errors'
import { useFeatureFlag } from '@/models/flag'
import {
  fetchRosterDraft,
  type PolicyChangeRequest,
  policyChangeWrites,
  usePolicyChangeMutation,
} from '@/models/policyChange'
import { POLICY_CHANGES_FLAG } from './PolicyChangesGate'

/**
 * After a change on the Trucks, Trailers or Drivers page, ask the customer to send it as a policy change
 * or take it back. Closing the dialog keeps the draft for later.
 */
export function useRosterChangePrompt(orgId: string | undefined) {
  const enabled = useFeatureFlag(POLICY_CHANGES_FLAG)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const cancel = usePolicyChangeMutation(orgId, policyChangeWrites.cancel)
  const [draft, setDraft] = useState<PolicyChangeRequest | null>(null)

  const afterRosterChange = useCallback(() => {
    if (!enabled || !orgId) return
    fetchRosterDraft(orgId).then(
      (found) => setDraft(found?.changes.length ? found : null),
      () => toast.error('Your change is saved. Send it to your insurance broker from Policy changes.')
    )
  }, [enabled, orgId])

  async function cancelChanges() {
    if (!draft) return
    try {
      await cancel.mutateAsync(draft.id)
      for (const key of [CacheKeys.TRUCKS, CacheKeys.TRAILERS, CacheKeys.DRIVERS]) {
        void queryClient.invalidateQueries({ queryKey: [key, orgId] })
      }
      setDraft(null)
      toast.success('Your change was canceled. Nothing changed on your policies.')
    } catch (error) {
      toast.error(parseError(error))
    }
  }

  const count = draft?.changes.length ?? 0
  const prompt = (
    <Dialog open={!!draft} onOpenChange={(open) => !open && setDraft(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send this to your insurance broker?</DialogTitle>
          <DialogDescription>
            Changes to your trucks, trailers and drivers go on your policies once your insurance broker
            reviews them and you accept the quote. {count === 1 ? '1 change is' : `${count} changes are`}{' '}
            waiting in your request.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={cancelChanges}
            loading={cancel.isPending ? 'Canceling…' : undefined}
          >
            {count === 1 ? 'Cancel the change' : 'Cancel the changes'}
          </Button>
          <Button onClick={() => draft && navigate(`/insure/policy-changes/${draft.id}`)}>
            Review and send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { prompt, afterRosterChange }
}
